import { isBefore } from "date-fns";
import type { ChallengeStage } from "@/services/challenges/types/challenges.types";
import type { RealityInfo } from "@/services/reality/types/reality.types";
import type { InscriptionStatusResponse } from "@/services/inscription/types";
import { ParticipantUiPhase } from "@/services/inscription/types";
import { isMergedRegistrationComplete } from "@/utils/inscriptionPdcMerge";
import { isPastFirstRealityStage } from "@/utils/registrationProfile";

/** Etapa 1 (order=1) no Reality atual — id fixo no banco. */
export const FIRST_REALITY_STAGE_ID = 2;

function parseRealityDate(
  value: string | Date | null | undefined
): Date | null {
  if (value == null || value === "") return null;
  const parsed = value instanceof Date ? value : new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** True when the configured deadline is strictly before `now` (deadline has passed). */
export function hasRealityDeadlinePassed(
  value: string | Date | null | undefined,
  now: Date = new Date()
): boolean {
  const parsed = parseRealityDate(value);
  if (!parsed) return false;
  return isBefore(parsed, now);
}

/** Between `finalChallengesDate` and `finalistsAnnouncementAt` (analysis window). */
export function isWithinRealityAnalysisWindow(
  realityInfo: Pick<
    RealityInfo,
    "finalChallengesDate" | "finalistsAnnouncementAt"
  > | null | undefined,
  now: Date = new Date()
): boolean {
  if (!hasRealityDeadlinePassed(realityInfo?.finalChallengesDate, now)) {
    return false;
  }
  const finalistsAt =
    realityInfo?.finalistsAnnouncementAt ??
    null;
  if (!finalistsAt) return true;
  return !hasRealityDeadlinePassed(finalistsAt, now);
}

/** Etapa 1 encerrada no backoffice ou pela data de `endDate` (id {@link FIRST_REALITY_STAGE_ID}). */
export function isFirstRealityStageClosed(
  inscription: InscriptionStatusResponse | null | undefined,
  stages?: ChallengeStage[] | null,
  now: Date = new Date()
): boolean {
  if (inscription?.firstStageClosed) return true;
  const firstStage = stages?.find((s) => s.id === FIRST_REALITY_STAGE_ID);
  if (!firstStage) return false;
  if (firstStage.isClosed) return true;
  return hasRealityDeadlinePassed(firstStage.endDate, now);
}

/**
 * Pós-1ª etapa (classificado / avançado): não exibir modais de prazo de inscrição
 * (`finalChallengesDate`); o fluxo passa pelos modais de aprovado/reprovado pós-avanço.
 * Com a etapa 1 (id=2) já encerrada, quem não está mais nela também pula esse bloqueio.
 */
export function shouldBypassInscriptionDeadlineBlocking(
  inscription: InscriptionStatusResponse | null | undefined,
  currentStageNumber: number,
  options?: {
    currentStageId?: number;
    stages?: ChallengeStage[];
  }
): boolean {
  if (!inscription) return false;
  if (
    isPastFirstRealityStage(
      currentStageNumber,
      inscription.advancedToResultStage
    )
  ) {
    return true;
  }
  if (!isFirstRealityStageClosed(inscription, options?.stages)) {
    return false;
  }
  if (inscription.advancedToResultStage) return true;
  if (currentStageNumber > 1) return true;
  const currentStageId = options?.currentStageId;
  if (
    currentStageId !== undefined &&
    currentStageId !== FIRST_REALITY_STAGE_ID
  ) {
    return true;
  }
  return false;
}

/** After `finalChallengesDate`, block UI for users who never finished registration. */
export function shouldBlockIncompleteRegistration(
  inscription: InscriptionStatusResponse | null | undefined,
  realityInfo: RealityInfo | null | undefined,
  isRegistrationComplete: boolean
): boolean {
  if (!inscription || !realityInfo?.finalChallengesDate) return false;
  if (isRegistrationComplete) return false;
  if (inscription.advancedToResultStage) return false;
  if (inscription.firstStageClosed) return false;
  if (inscription.participantUiPhase !== ParticipantUiPhase.NONE) return false;
  return hasRealityDeadlinePassed(realityInfo.finalChallengesDate);
}

type ResolveMessageParams = {
  realityInfo: RealityInfo | null | undefined;
  isRegistrationComplete: boolean;
  participantUiPhase?: ParticipantUiPhase;
  now?: Date;
};

function treatsRegistrationAsCompleteForMessage(
  isRegistrationComplete: boolean,
  participantUiPhase?: ParticipantUiPhase
): boolean {
  if (isRegistrationComplete) return true;
  return (
    participantUiPhase ===
      ParticipantUiPhase.INSCRIPTION_COMPLETED_CELEBRATION ||
    participantUiPhase === ParticipantUiPhase.WAITING_FINALISTS_ANNOUNCEMENT
  );
}

/** Analysis period is over (finalists announcement date has passed). */
export function isRealityAnalysisPeriodEnded(
  realityInfo: Pick<RealityInfo, "finalistsAnnouncementAt"> | null | undefined,
  now: Date = new Date()
): boolean {
  return hasRealityDeadlinePassed(
    realityInfo?.finalistsAnnouncementAt ?? null,
    now
  );
}

/**
 * Three blocking-dialog messages (celebration / waiting — same logout behavior):
 * 1. Registration complete — success, wait for finalists.
 * 2. Incomplete, challenges closed, still before finalists date — results soon.
 * 3. Finalists announcement date passed — wait for results disclosure.
 */
export function resolveParticipantBlockingDialogText({
  realityInfo,
  isRegistrationComplete,
  participantUiPhase,
  now = new Date(),
}: ResolveMessageParams): string {
  if (
    treatsRegistrationAsCompleteForMessage(
      isRegistrationComplete,
      participantUiPhase
    )
  ) {
    return "Sua inscrição para o Reality foi finalizada com sucesso! A divulgação dos finalistas será comunicada em breve.";
  }

  if (isRealityAnalysisPeriodEnded(realityInfo, now)) {
    return "O período de análise foi encerrado. Aguarde a divulgação dos resultados.";
  }

  return "As inscrições estão encerradas. Em breve divulgaremos os classificados.";
}

export function isRegistrationCompleteForBlocking(
  inscription: InscriptionStatusResponse | null | undefined,
  pdcCourses: Parameters<typeof isMergedRegistrationComplete>[1],
  usePdcList: boolean
): boolean {
  return isMergedRegistrationComplete(inscription, pdcCourses, usePdcList);
}
